export default {
    props: {
        markers: {
            type: Array,
            description: "contains data for marker layer: [{name: string, position: [lng,lat]}]"
        },
        clusterSource: {
            type: Object,
            description: "GeoJSON data for clustered map layer"
        },
        defaultMapPosition: {
            type: Array,
            default() {
                return [10.255079, 50.888873]
            }
        },
        defaultMarkerName: {
            type: String,
            default: 'Kein Ort gefunden'
        },
        download: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uid: null,
            popup: null,
            hoveredFeature: null,
            layerAnimationLoop: null
        }
    },
    methods: {
        downloadMap() {
            const url = this.map.getCanvas().toDataURL('image/png')
            const a = document.createElement('a');
            a.href = url;
            a.download = 'amosweb-map.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        async loadMapbox() {
            if (!this.mapbox) this.mapbox = (await import(/* webpackChunkName: "mapboxgl" */'mapbox-gl')).default
            return this.mapbox
        },
        addClusterLayers() {
            let self = this

            this.map.addSource('events', {
                type: 'geojson',
                data: this.clusterSource || [],
                cluster: true,
                clusterMaxZoom: 14,
                clusterRadius: 50
            })

            this.map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'events',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': ['step', ['get', 'point_count'], '#09be8b', 10, '#2dc6ce', 25, '#f688d9'],
                    'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 25, 30],
                    'circle-blur': 0.2
                }
            })

            this.map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'events',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-size': 14
                }
            })

            this.map.addLayer({
                    id: 'unclustered-point',
                    type: 'symbol',
                    source: 'events',
                    filter: ['!', ['has', 'point_count']],
                    layout: {
                        'icon-image': [
                            'case',
                            ['==', ['get', 'typeId'], 1],
                            'travel',
                            ['==', ['get', 'typeId'], 2],
                            'seminar',
                            ['==', ['get', 'typeId'], 4],
                            'event',
                            'mapbox-marker-icon-red'],
                        'icon-ignore-placement': true,
                        'icon-size': [
                            'case',
                            ['==', ['get', 'typeId'], 1],
                            0.1,
                            ['==', ['get', 'typeId'], 2],
                            0.1,
                            ['==', ['get', 'typeId'], 4],
                            0.1,
                            1],
                    }
                }
            )

            // inspect a cluster on click
            this.map.on('click', 'clusters', (e) => {
                const features = self.map.queryRenderedFeatures(e.point, {
                    layers: ['clusters']
                });
                const clusterId = features[0].properties.cluster_id;
                self.map.getSource('events').getClusterExpansionZoom(
                    clusterId,
                    (err, zoom) => {
                        if (err) return;
                        self.map.easeTo({
                            center: features[0].geometry.coordinates,
                            zoom: zoom
                        });
                    }
                );
            });

            this.map.on('click', 'unclustered-point', (e) => {
                const offerNumber = e.features[0].properties.offerNumber;
                self.$emit('clickedEvent', {offerNumber})
            });

            this.map.on('mouseenter', 'clusters', () => {
                self.map.getCanvas().style.cursor = 'pointer';
            });

            this.map.on('mouseleave', 'clusters', () => {
                self.map.getCanvas().style.cursor = '';
            });

            this.map.on('mouseenter', 'unclustered-point', () => {
                self.map.getCanvas().style.cursor = 'pointer';
            });

            this.map.on('mousemove', 'unclustered-point', (e) => {
                if (e.features[0]?.properties?.id !== this.hoveredFeature?.id) {
                    self.popup?.remove()
                    const coordinates = e.features[0].geometry.coordinates.slice();
                    let hoveredFeature = e.features[0].properties
                    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                    }

                    self.popup = new self.mapbox.Popup({
                        closeButton: false,
                        closeOnClick: false,
                        offset: 20
                    }).setLngLat(coordinates)
                        .setHTML(this.generatePopUpHtmlForFeature(hoveredFeature))
                        .addTo(self.map);
                }
            });

            this.map.on('mouseleave', 'unclustered-point', () => {
                self.map.getCanvas().style.cursor = '';
                self.popup?.remove()
            });

            this.adjustMapBoundsToFitAllPoints(this.clusterSource?.features.map(feature => feature.geometry.coordinates))
        },
        addMarkerLayers() {
            if (!this.markers?.length) return
            for (let markerData of this.markers) {
                new this.mapbox.Marker({
                    color: markerData.position ? '#f5365c' : '#d5d5d5',
                    draggable: false
                }).setLngLat(
                    this.markerPositionIsValid(markerData) ? markerData.position : this.defaultMapPosition
                ).setPopup(new this.mapbox.Popup({
                    closeButton: false,
                    closeOnMove: true
                }).setHTML(
                    this.generatePopUpHtmlForMarker(markerData)
                )).addTo(this.map);
            }
            this.adjustMapBoundsToFitAllPoints(this.markers.map(markerData => markerData.position))
        },
        markerPositionIsValid(markerData) {
            if (!markerData.position) return false
            return !!markerData.position[0] && !!markerData.position[1]
        },
        generatePopUpHtmlForMarker(markerData) {
            if (!markerData.name && !markerData.address) return ""
            let html = `<div class="text-center">`
            if (markerData.name) {
                html += `<div>
                            <h4 class="mb-0 font-weight-bold">${markerData.name}</h4>`
                if (markerData.address)
                    html += `<div>
                                <small class="popup-address">${markerData.address}</small>
                             </div>`
                html += `</div>`
            }
            html += `</div>`
            return html
        },
        generatePopUpHtmlForFeature(hoveredFeature) {
            let html = `<div class="text-center">`
            if (hoveredFeature.image) {
                html += `<div class="mb-2 mx--2 mt--2 popup-image-container">
                            <img
                                src="${hoveredFeature.image}"
                                alt="Image placeholder"
                                class="popup-image">`
                if (hoveredFeature.imageLicence)
                    html += `<div class="image-licence" title="${hoveredFeature.imageLicence}">
                                <span style="white-space: nowrap">
                                    ${hoveredFeature.imageLicence}
                                </span>
                            </div>`
                html += `</div>`
            }
            if (hoveredFeature.date) {
                html += `<div>
                            <small class="font-weight-600">${hoveredFeature.date}</small>
                         </div>`
            }
            if (hoveredFeature.name) {
                html += `<div class="mb-2">
                            <h3 class="font-weight-bold mb-0">${hoveredFeature.name}</h3>`
                html += `</div>`
            }
            if (hoveredFeature.location) {
                html += `<div>
                            <h4 class="mb-0 font-weight-bold w-100 d-flex gap-2 align-items-center justify-content-center">
                                <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 384 512"><!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. --><path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"/></svg>
                                ${hoveredFeature.location}
                            </h4>`
                if (hoveredFeature.district) {
                    html += `<div>
                                <span>${hoveredFeature.district}</span>
                             </div>`
                }
                if (hoveredFeature.address) {
                    html += `<div>
                                <small class="popup-address">${hoveredFeature.address}</small>
                             </div>`
                }
                html += `</div>`
                if (hoveredFeature.lowBarrier) {
                    html += `<div class="mt-2">
                                <span class="badge badge-pill badge-success">Barrierearm</span>
                             </div>`
                }
            }
            html += `</div>`
            return html
        },
        adjustMapBoundsToFitAllPoints(coordinates) {
            if (!coordinates?.length) return
            if (coordinates.length === 1)
                this.map?.flyTo({
                    center: coordinates[0],
                    zoom: 12
                });
            else {
                let bounds = new this.mapbox.LngLatBounds();
                this.clusterSource.features.forEach(function (feature) {
                    bounds.extend(feature.geometry.coordinates);
                });
                this.map?.fitBounds(bounds, {padding: 100})
            }
        },
        stopLayerAnimations() {
            if (this.layerAnimationLoop) clearInterval(this.layerAnimationLoop)
        }
    }
}
